// Custom hook to check for media queries
/**
 * Breakpoints configuration:
 * $breakpoints: (
 *   sm: 400px,  // Small phone
 *   md: 768px,  // Tablet
 *   lg: 1024px, // Small laptop
 *   xl: 1280px, // Large laptop
 *   "2xl": 1920px // Desktop
 * );
 */

import { useState, useEffect } from "react";

const breakpoints = {
  sm: 400,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1920,
};

const getMatches = (breakpointKey, isUp) => {
  if (typeof window === "undefined") {
    return true;
  }
  const mediaQuery = window.matchMedia(`(min-width: ${breakpoints[breakpointKey]}px)`);
  return isUp ? mediaQuery.matches : !mediaQuery.matches;
};

/**
 *
 * @param {keyof breakpoints} breakpointKey - The key of the breakpoint to match against, e.g., 'sm', 'md', 'lg', 'xl', or '2xl'.
 * @param {boolean} [isUp=true] - If true, matches when the viewport is larger than or equal to the breakpoint; if false, matches when it is smaller.
 * @returns {boolean} - Whether the media query matches.
 */
export default function useMediaQuery(breakpointKey, isUp = true) {
  // Always initialise with `true` (the SSR assumption) so the first client
  // render matches the server-rendered HTML and avoids hydration mismatches.
  const [matches, setMatches] = useState(true);

  useEffect(() => {
    const handleChange = () => {
      setMatches(getMatches(breakpointKey, isUp));
    };

    // Sync with actual viewport on mount
    handleChange();

    const mediaQuery = window.matchMedia(`(min-width: ${breakpoints[breakpointKey]}px)`);
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, [breakpointKey, isUp]);

  return matches;
}
